/**
 * Which model each AI agent runs on.
 *
 * `GET /api/agent-models` is open to every member (unlike `/api/agents`,
 * which is `manage-agents` only) because the model is part of how an agent
 * is shown to the people working alongside it. It includes deactivated
 * agents, so an old comment by a retired agent still names its model, and
 * answers `[]` while the agents feature is off.
 *
 * The query is gated on the workspace's agents switch from `/api/me`, so a
 * workspace without agents never makes the call. One shared cache entry
 * feeds every `<AiTag>` on screen.
 */

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { apiClient } from '@/api/client'
import type { AgentModelRow } from '@/api/types-bridge'
import { useMe } from '@/features/me/use-me'
import { modelLabel } from '@/lib/model-label'

export const AGENT_MODELS_QUERY_KEY = ['agent-models'] as const

const EMPTY: ReadonlyMap<string, string> = new Map()

/** `userId` → raw model id, or an empty map until it loads / when agents are off. */
export function useAgentModels(): ReadonlyMap<string, string> {
  const me = useMe()
  const enabled = me.data?.features?.agents === true
  const query = useQuery({
    queryKey: AGENT_MODELS_QUERY_KEY,
    queryFn: async () => {
      const res = await apiClient<{ data: readonly AgentModelRow[] }>('/api/agent-models')
      return res.data
    },
    enabled,
    // Refetched when the tab regains focus, so a model an admin changed elsewhere shows up
    // without a reload; the payload is one small query.
    staleTime: 5 * 60_000,
    retry: false,
  })
  return useMemo(() => {
    if (!query.data) return EMPTY
    return new Map(query.data.map((row) => [row.userId, row.model] as const))
  }, [query.data])
}

/** The raw model id for one agent, or `undefined` when unknown. */
export function useAgentModel(userId: string | null | undefined): string | undefined {
  const models = useAgentModels()
  return userId ? models.get(userId) : undefined
}

/**
 * The readable model name for one agent out of an already-loaded map, for
 * list builders that compose a row's secondary line as text (where a
 * component can't go). `null` when the user isn't an agent or the model is
 * unknown.
 */
export function agentModelText(
  models: ReadonlyMap<string, string>,
  userId: string | null | undefined,
): string | null {
  const model = userId ? models.get(userId) : undefined
  return model ? modelLabel(model) : null
}
